import * as React from 'react';
import { observer } from 'mobx-react';
import { Container } from 'reactstrap';
import ReactResizeDetector from 'react-resize-detector';

import { PrincipalConfig } from '@app/AppConstants/PrincipalConfig';
import { KeyboardKeys } from '@app/AppConstants/KeyboardKeys';

import { appStore } from '../AppStore';
import { configurationStore } from '@app/Stores/ConfigurationStore';
import { navigationStore } from '@app/Stores/NavigationStore';
import { loaderStore } from '@app/Stores/LoaderStore';

import { Loading } from './Loading';
import { NavMenu } from './NavMenu';
import { Modals } from './Modal';
import MaintenanceBar from '@app/Components/MaintenanceBar';
import EasaAlertBar from '@app/Components/EasaAlertBar';
import { isTouchDevice } from '@app/Helpers/DevicesHelper';
import { applyDecorators } from "@app/Helpers/Decorator";
import { AzureAuth, UserPermissions } from './AzureAuth';
import { Configurations } from '@app/AppConstants/Configurations';

export type LayoutProps = {
    children?: React.ReactNode;
};

@observer
export class Layout extends React.Component<LayoutProps> {

    constructor(props: LayoutProps) {
        super(props);
        applyDecorators(this);
    }

    async componentDidMount() {
        document.addEventListener('keydown', this._keydownHandler, true);

        const isShortcutsEnabled = await configurationStore.get(PrincipalConfig.CommonGroup, PrincipalConfig.UseKeyboardShortcutsKey);
        appStore.isShortcutsEnabled = isShortcutsEnabled ? !!JSON.parse(isShortcutsEnabled) : false;
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this._keydownHandler);
    }

    public render() {
        const className = 'content-wrapper' + (this.isTouchDevice ? ' touchable' : '') + (appStore.isContrastMode ? ' contrast-mode' : '');

        const isCrewPortal = navigationStore.currentLocation.pathname.startsWith('/crew-portal')
            || navigationStore.currentLocation.pathname.startsWith('/request-tool') && !navigationStore.currentLocation.pathname.startsWith('/request-tool-')
            || appStore.isCrewPortalDns;

        const hasEasaPopupAccess = AzureAuth.hasAccess([UserPermissions.RosterCabinWriteTracking, UserPermissions.RosterCockpitWriteTracking]);

        return (
            <div className={className} key="wrapper">
                <Loading fullpage progress={loaderStore.globalProgress} />
                <div>
                    <MaintenanceBar />
                    {!isCrewPortal && configurationStore.isFeatureEnabled(Configurations.enableEasaWarning) && hasEasaPopupAccess && <EasaAlertBar />}
                    <ReactResizeDetector handleHeight onResize={this._onResizeAlertsContainer} />
                </div>
                {!isCrewPortal && <NavMenu />}
                <Container fluid>{this.props.children}</Container>
                <Modals />
            </div>
        );
    }

    private get isTouchDevice() {
        if (isTouchDevice) {
            return true;
        }

        // include the 'heartz' as a way to have a non matching MQ to help terminate the join
        // https://git.io/vznFH
        const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
        const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
        const list: MediaQueryList = window.matchMedia(query);
        return list.matches;
    }

    _keydownHandler = (event: KeyboardEvent) => {
        if (appStore.isShortcutsEnabled && appStore.isCtrlKey && event.keyCode === KeyboardKeys.H) {
            if (!navigationStore.isHomePage) {
                navigationStore.redirect('/');
            }
            event.preventDefault();
            return false;
        }
        return true;
    };

    private _onResizeAlertsContainer = (_?: number, height?: number) => {
        document.body.style.setProperty('--alert-bar-height', height + 'px');
        window.dispatchEvent(new Event('resize'));
    };
}
